import { throttle } from 'lodash';
import { queryClient } from 'src/query-client';

type FeatureKind =
  | 'network_site'
  | 'subscriber_site'
  | 'ptp_link'
  | 'access_point'
  | 'mesh_link'
  | 'pmp_link';

export function createNetworkSiteTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('network_site', projectId);
}

export function createSubscriberSiteTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('subscriber_site', projectId);
}

export function createPTPTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('ptp_link', projectId);
}

export function createAPTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('access_point', projectId);
}

export function createMeshTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('mesh_link', projectId);
}

export function createPMPLinkTableQueryKey(projectId: string): string[] {
  return getTableQueryKey('pmp_link', projectId);
}

export function getTableQueryKey(
  kind: FeatureKind,
  projectId: string
): string[] {
  return ['project', projectId, kind, 'table'];
}

export function deleteFromTable(
  kind: FeatureKind,
  projectId: string,
  idsToDelete: string[]
) {
  const queryKey = getTableQueryKey(kind, projectId);

  if (queryKey) {
    queryClient.setQueryData(queryKey, (prev: any) => {
      if (prev != null) {
        const deletedIds = new Set(idsToDelete);
        return {
          ...prev,
          rows: prev.rows.filter((r: any) => !deletedIds.has(r.id)),
        };
      }
    });
  }
}

// export const refreshTable = throttle(
//   (kind: FeatureKind, projectId: string) => {
//     const queryKey = getTableQueryKey(kind, projectId);
//     queryClient.invalidateQueries(queryKey);
//   },
//   500
//   //   { leading: false }
//   //   { trailing: false }
// );
function mergeCustomFieldData(oldRow: any, newRow: any) {
  if (!oldRow) return newRow;

  const merged = { ...oldRow, ...newRow };

  // Socket column payloads often omit custom field values. Prefer values
  // from the new row when present; otherwise keep the cached ones.
  const newCf = newRow?.properties?.custom_fields;
  const oldCf = oldRow?.properties?.custom_fields;
  if (newCf != null) {
    merged.properties = {
      ...(oldRow.properties || {}),
      ...(newRow.properties || {}),
      custom_fields: newCf,
    };
  } else if (oldCf != null) {
    merged.properties = {
      ...(oldRow.properties || {}),
      ...(newRow.properties || {}),
      custom_fields: oldCf,
    };
  }

  for (const side of ['local', 'remote'] as const) {
    if (!newRow?.[side] && !oldRow?.[side]) continue;
    const newSideCf = newRow?.[side]?.custom_fields;
    const oldSideCf = oldRow?.[side]?.custom_fields;
    merged[side] = {
      ...(oldRow?.[side] || {}),
      ...(newRow?.[side] || {}),
    };
    if (newSideCf != null) {
      merged[side].custom_fields = newSideCf;
    } else if (oldSideCf != null) {
      merged[side].custom_fields = oldSideCf;
    }
  }

  return merged;
}

export function refreshTable(
  kind: FeatureKind,
  projectId: string,
  newRows?: Object[]
) {
  const queryKey = getTableQueryKey(kind, projectId);
  if (newRows && newRows.length > 0) {
    // The socket message has included the columns along with the geometry so
    // it is possible to refresh the table rather than requesting the
    // entire table again
    queryClient.setQueryData(queryKey, (prev) => {
      if (prev != null) {
        let rows = [];
        if (kind === 'pmp_link') {
          const rowIds = newRows.map((r: any) => r.remote_end_id);
          const oldById = new Map(
            prev.rows.map((r: any) => [r.remote_end_id, r])
          );
          rows = prev.rows.filter((r) => !rowIds.includes(r.remote_end_id));
          rows.push(
            ...newRows.map((r: any) =>
              mergeCustomFieldData(oldById.get(r.remote_end_id), r)
            )
          );
        } else {
          const rowIds = newRows.map((r: any) => r.id);
          const oldById = new Map(prev.rows.map((r: any) => [r.id, r]));
          rows = prev.rows.filter((r) => !rowIds.includes(r.id));
          rows.push(
            ...newRows.map((r: any) => mergeCustomFieldData(oldById.get(r.id), r))
          );
        }
        return { ...prev, rows };
      }
    });
    return;
  }

  // fall-back to refresh the whole table
  queryClient.invalidateQueries({ queryKey });
}

export function getSiteGraphQueryKey(
  projectId: string,
  siteId: string
): string[] {
  return [projectId, 'site', siteId, 'graph'];
}

export function refreshSiteGraphTable(projectId: string, siteId: string) {
  const queryKey = getSiteGraphQueryKey(projectId, siteId);
  queryClient.invalidateQueries(queryKey);
}
